import React, { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { navLinks } from "./mock";
import { Menu, X } from "lucide-react";
import ChatWidget from "./components/ChatWidget";
import { Seo } from "./components/Seo";

export const Layout = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const isDarkBgAtTop = location.pathname === '/' || location.pathname === '/chi-siamo' || location.pathname.startsWith('/prodotti/');
  const textColorClass = !scrolled && isDarkBgAtTop ? "text-white" : "text-black";

  useEffect(() => {
    setIsOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white text-black font-sans flex flex-col selection:bg-black selection:text-white">
      <Seo />
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          scrolled ? "bg-white/90 backdrop-blur-md py-3 shadow-sm text-black" : `bg-transparent py-5 ${textColorClass}`
        }`}
      >
        <div className="w-full px-4 md:px-8 lg:px-12 flex justify-between items-center">
          <Link to="/" className="z-50 flex items-center -ml-2 md:-ml-4">
            <img 
              src="/media/logo-izosmart.png" 
              alt="Izosmart" 
              className={`h-8 md:h-10 w-auto object-contain transition-all duration-300 ${scrolled || !isDarkBgAtTop ? 'mix-blend-multiply' : 'drop-shadow-[0_0_15px_rgba(255,255,255,0.9)]'}`} 
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.path}
                className="text-sm font-medium hover:text-[#7dba00] transition-colors uppercase tracking-widest"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Nav Toggle */}
          <button
            className="lg:hidden z-50"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Nav Menu */}
        <div
          className={`fixed inset-0 bg-white text-black flex flex-col items-center justify-center gap-8 transition-transform duration-500 lg:hidden z-40 ${
            isOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.path}
              onClick={() => setIsOpen(false)}
              className="text-3xl font-bold uppercase tracking-widest text-black hover:text-[#7dba00] transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </nav>

      <main className="flex-grow">
        <Outlet />
      </main>

      <footer className="bg-black text-white py-24 mt-auto">
        <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <h3 className="text-2xl font-bold mb-8 tracking-tighter uppercase">Izosmart</h3>
            
            <div>
              <h4 className="font-bold uppercase text-xs tracking-widest text-gray-500 mb-6">I Nostri Partner</h4>
              <div className="flex flex-col gap-8 opacity-80 hover:opacity-100 transition-opacity duration-300">
                <img 
                  src="/media/loghi-theatro-colore-schuco.png" 
                  alt="Partner Schüco Theatro" 
                  className="h-12 w-auto object-contain brightness-0 invert self-start"
                />
                <img 
                  src="/media/logo-maico-1.webp" 
                  alt="Partner Maico" 
                  className="h-16 w-auto object-contain self-start"
                />
              </div>
              <div data-testid="partner-inotherm" className="mt-8 flex flex-col gap-4 max-w-xs">
                <span className="self-start bg-white px-4 py-3">
                  <img
                    src="/media/logo-inotherm.webp"
                    alt="Inotherm – porte d'ingresso in alluminio"
                    width="1112"
                    height="156"
                    className="h-6 w-auto"
                    data-testid="partner-inotherm-logo"
                  />
                </span>
                <p className="text-sm text-gray-400 font-light leading-relaxed">
                  Porte d'ingresso in alluminio Inotherm, disponibili tramite Izosmart. Progetta la tua porta online e chiedici un preventivo.
                </p>
                <a
                  href="https://doordesigner.inotherm.it/konfigurator/lang/it/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="partner-inotherm-configurator-link"
                  className="self-start border border-[#7dba00] text-[#7dba00] hover:bg-[#7dba00] hover:text-black px-5 py-3 uppercase text-xs font-bold tracking-[0.15em] transition-colors"
                >
                  Configura la tua porta Inotherm
                </a>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-bold mb-4 uppercase text-sm tracking-widest">Esplora</h4>
            <ul className="space-y-3 text-gray-400 font-light">
              {navLinks.map((link) => (
                <li key={link.label}>
                  <Link to={link.path} className="hover:text-[#7dba00] transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-bold mb-4 uppercase text-sm tracking-widest">Contatti</h4>
            <ul className="space-y-3 text-gray-400 font-light">
              <li>Viale al Parco 3, Avio</li>
              <li>Sabbionara, Italia</li>
              <li><a href="mailto:info@izosmart.com" data-testid="footer-email" className="hover:text-[#7dba00] transition-colors">info@izosmart.com</a></li>
              <li>Sede Avio: <a href="tel:+393387010065" data-testid="footer-phone-sede-avio" className="hover:text-[#7dba00] transition-colors">+39 338 7010065</a></li>
              <li>Commerciale: <a href="tel:+393509500280" data-testid="footer-phone-commerciale" className="hover:text-[#7dba00] transition-colors">+39 350 950 0280</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold mb-4 uppercase text-sm tracking-widest">Seguici</h4>
            <ul className="space-y-3 text-gray-400 font-light">
              <li><a href="https://www.facebook.com/people/Izosmart-Trentino-Serramenti-in-PVC-Schuco/61583217208754/" target="_blank" rel="noopener noreferrer" className="hover:text-[#7dba00] transition-colors">Facebook</a></li>
              <li><a href="#" className="hover:text-[#7dba00] transition-colors">Instagram</a></li>
              <li><a href="https://www.linkedin.com/company/izosmartitalia/?originalSubdomain=it" target="_blank" rel="noopener noreferrer" className="hover:text-[#7dba00] transition-colors">LinkedIn</a></li>
            </ul>
          </div>
        </div>
        <div className="container mx-auto px-6 lg:px-12 mt-24 pt-8 border-t border-gray-800 text-gray-500 text-sm flex flex-col md:flex-row justify-between md:items-center gap-4">
          <p data-testid="footer-legal">Izosmart S.r.l. · Viale al Parco 3, 38063 Avio (TN) · C.F. e P. IVA 02723050221 · REA TN-245275</p>
          <p className="flex gap-6">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#7dba00] transition-colors">Privacy</Link>
            <span>&copy; {new Date().getFullYear()} Izosmart S.r.l.</span>
          </p>
        </div>
      </footer>
      {/* Chatbot Widget */}
      <ChatWidget />
    </div>
  );
};
